import { useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box, Button, Card, CardContent, Chip, Divider,
  Sheet, Stack, Typography, CircularProgress,
  IconButton, Tooltip,
} from "@mui/joy";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import SecurityRoundedIcon from "@mui/icons-material/SecurityRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";

import { usePermissions } from "../../usePermissions";
import { LayoutContext } from "../../context";
import { getDisplayName } from "../../shared/nameUtils";
import { APIGetChildAccounts, APIDeleteChildAccount, APIUpdateChildAccount } from "../../api_access";
import ChildAccountModal from "../../components/modals/ChildAccountModal";


// ── Карточка сотрудника ────────────────────────────────────────────
function ChildAccountCard({ child, idx, onEdit, onBlock, onDelete, canEditAccess = true, canDeleteAccess = true }) {
  const isBlocked = child.is_blocked;
  const isInactive = !child.is_active;
  const activePerms = (child.permissions || []).filter(p => p.is_active);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16, scale: 0.95 }}
      transition={{ delay: idx * 0.06, duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
    >
      <Card
        variant="outlined"
        sx={{
          borderRadius: "18px",
          transition: "box-shadow 0.2s, transform 0.2s",
          "&:hover": {
            boxShadow: "0 8px 32px rgba(99,102,241,0.1)",
            transform: "translateY(-2px)",
          },
          overflow: "hidden",
          p: 0, gap: 0,
          opacity: isBlocked || isInactive ? 0.65 : 1,
        }}
      >
        {/* Цветная полоска сверху */}
        <Box
          sx={{
            height: 3,
            background: isBlocked
              ? "linear-gradient(90deg, #ef4444, #f87171)"
              : "linear-gradient(90deg, #6366f1, #8b5cf6)",
          }}
        />

        <CardContent sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            {/* Левая часть: аватар + данные */}
            <Stack direction="row" alignItems={{ xs: 'flex-start', sm: "center" }} spacing={2} sx={{ minWidth: 0, flex: 1 }}>
              <Sheet
                variant="soft"
                color={isBlocked ? "danger" : "primary"}
                sx={{
                  width: 52, height: 52, borderRadius: "16px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0, fontSize: "1.5rem",
                }}
              >
                {getDisplayName(child)
                  ? getDisplayName(child).charAt(0).toUpperCase()
                  : <PersonRoundedIcon />
                }
              </Sheet>

              <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1, minWidth: 0, width: '100%', justifyContent: 'space-between' }}>
                <Stack direction={'column'}>
                  <Stack direction="column" alignItems="flex-start" spacing={0.2}>
                    <Typography level="title-sm" fontWeight={700} noWrap>
                      {getDisplayName(child) || "Без имени"}
                    </Typography>
                    {isBlocked && (
                      <Chip size="sm" variant="solid" color="danger" sx={{ fontSize: 10 }}>
                        Заблокирован
                      </Chip>
                    )}
                    {!isBlocked && !isInactive && (
                      <Chip size="sm" variant="soft" color="success" sx={{ fontSize: 10 }}>
                        Активен
                      </Chip>
                    )}
                  </Stack>

                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                    <PhoneRoundedIcon sx={{ fontSize: 13, color: "text.tertiary" }} />
                    <Typography level="body-xs" color="neutral">
                      {child.phone}
                    </Typography>
                  </Stack>

                  {child.email && (
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.3 }}>
                      <EmailRoundedIcon sx={{ fontSize: 13, color: "text.tertiary" }} />
                      <Typography level="body-xs" color="neutral">
                        {child.email}
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {/* Кнопки действий */}
                <Stack direction="row" spacing={0.5} sx={{ height: "fit-content" }}>
                  {canEditAccess && (
                      <Tooltip title="Редактировать" size="sm">
                      <IconButton
                          size="sm" variant="soft" color="neutral"
                          onClick={() => onEdit(child)}
                          sx={{ borderRadius: "10px" }}
                      >
                          <EditRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      </Tooltip>
                  )}

                  {canEditAccess && (
                      <Tooltip title={isBlocked ? "Разблокировать" : "Заблокировать"} size="sm">
                      <IconButton
                          size="sm" variant="soft"
                          color={isBlocked ? "success" : "warning"}
                          onClick={() => onBlock(child)}
                          sx={{ borderRadius: "10px" }}
                      >
                          {isBlocked
                          ? <LockOpenRoundedIcon sx={{ fontSize: 18 }} />
                          : <BlockRoundedIcon sx={{ fontSize: 18 }} />
                          }
                      </IconButton>
                      </Tooltip>
                  )}

                  {canDeleteAccess && (
                      <Tooltip title="Удалить" size="sm">
                      <IconButton
                          size="sm" variant="soft" color="danger"
                          onClick={() => onDelete(child)}
                          sx={{ borderRadius: "10px" }}
                      >
                          <DeleteRoundedIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      </Tooltip>
                  )}
                </Stack>
              </Stack>

            </Stack>
          </Stack>

          {/* Права */}
          {activePerms.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.8 }}>
                <SecurityRoundedIcon sx={{ fontSize: 13, color: "text.tertiary" }} />
                <Typography
                  level="body-xs" color="neutral" fontWeight={700}
                  sx={{ textTransform: "uppercase", letterSpacing: "0.06em" }}
                >
                  Права доступа
                </Typography>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={0.6}>
                {activePerms.map(p => (
                  <Chip
                    key={p.permission_type}
                    size="sm" variant="soft" color="primary"
                    startDecorator={<CheckCircleRoundedIcon sx={{ fontSize: 12 }} />}
                    sx={{ fontSize: 11 }}
                  >
                    {p.permission_type_display}
                  </Chip>
                ))}
              </Stack>
            </Box>
          )}

          {/* Дата */}
          {child.date_create && (
            <Typography level="body-xs" color="neutral" sx={{ mt: 1.5 }}>
              Добавлен{" "}
              {new Date(child.date_create).toLocaleDateString("ru-RU", {
                day: "2-digit", month: "long", year: "numeric",
              })}
            </Typography>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}


// ── Empty state ───────────────────────────────────────────────────
function EmptyState({ onAdd, canAdd = true }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
      <Sheet
        variant="soft" color="neutral"
        sx={{
          borderRadius: "20px", p: 6,
          textAlign: "center",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 2,
        }}
      >
        <Sheet
          variant="soft" color="primary"
          sx={{
            width: 72, height: 72, borderRadius: "20px",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "2.2rem",
          }}
        >
          <GroupRoundedIcon sx={{ fontSize: 36 }} />
        </Sheet>
        <Box>
          <Typography level="title-md" fontWeight={700}>
            Нет сотрудников
          </Typography>
          <Typography level="body-sm" color="neutral" sx={{ mt: 0.5, maxWidth: 320 }}>
            Добавьте сотрудников, чтобы они могли работать с заявками от имени вашей компании
          </Typography>
        </Box>
        {canAdd && (
            <Button
                startDecorator={<AddRoundedIcon />}
                onClick={onAdd}
                size="lg"
                sx={{
                    mt: 1,
                    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                    boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                }}
            >
                Добавить сотрудника
            </Button>
        )}
      </Sheet>
    </motion.div>
  );
}


// ── Страница «Доступы» ─────────────────────────────────────────────
export default function AccessPage() {
  const { account } = useContext(LayoutContext);

  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editChild, setEditChild] = useState(null);

  const { hasPermission, isChildAccount } = usePermissions();
    const canAddAccess = hasPermission('add_access');
    const canEditAccess = hasPermission('edit_access');
    const canDeleteAccess = hasPermission('delete_access');
    const canEditPermissions = hasPermission('edit_access_permissions');


  // ── Загрузка списка ──────────────────────────────────────────────
  const loadChildren = async () => {
    setLoading(true);
    const result = await APIGetChildAccounts();
    if (result?.children) {
      setChildren(result.children);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadChildren();
  }, []);

  // ── Добавить ─────────────────────────────────────────────────────
  const handleAdd = () => {
    setEditChild(null);
    setModalOpen(true);
  };

  // ── Редактировать ────────────────────────────────────────────────
  const handleEdit = (child) => {
    setEditChild(child);
    setModalOpen(true);
  };

  // ── Заблокировать / Разблокировать ───────────────────────────────
  const handleBlock = async (child) => {
    const isBlocked = child.is_blocked;
    const message = isBlocked
      ? `Разблокировать сотрудника "${getDisplayName(child) || child.phone}"?`
      : `Заблокировать сотрудника "${getDisplayName(child) || child.phone}"? Он потеряет доступ к кабинету.`;

    if (!window.confirm(message)) return;

    const result = await APIUpdateChildAccount(child.id, {
      is_blocked: !isBlocked,
    });

    if (result?.status !== "error") {
      loadChildren();
    } else {
      alert(result.detail || "Ошибка");
    }
  };

  // ── Удалить ──────────────────────────────────────────────────────
  const handleDelete = async (child) => {
    const message = `Вы уверены, что хотите удалить сотрудника "${getDisplayName(child) || child.phone}"?\n\nАккаунт будет деактивирован и сотрудник потеряет доступ.`;

    if (!window.confirm(message)) return;

    const result = await APIDeleteChildAccount(child.id);

    if (result?.status !== "error") {
      loadChildren();
    } else {
      alert(result.detail || "Ошибка удаления");
    }
  };


  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: 3, pb: 8 }}>

        {/* Заголовок */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3.5 }}
            >
                <Box>
                    <Typography level="h2" sx={{ fontWeight: 800, letterSpacing: "-0.5px" }}>
                      Сотрудники 🔐
                    </Typography>
                    <Typography level="body-sm" color="neutral" sx={{ mt: 0.5 }}>
                      Управляйте сотрудниками и их правами
                    </Typography>
                </Box>
                {canAddAccess && (
                    <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                        <Button
                            startDecorator={<AddRoundedIcon />}
                            size="lg"
                            onClick={handleAdd}
                            sx={{
                                fontWeight: 700, flexShrink: 0, borderRadius: "12px",
                                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                                boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
                            }}
                        >
                            Добавить сотрудника
                        </Button>
                    </motion.div>
                )}
            </Stack>
        </motion.div>

      {/* Статистика */}
      {children.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
            gap: 1.5, mb: 3,
          }}
        >
          {[
            { label: "Всего", value: children.length, color: "primary" },
            { label: "Активных", value: children.filter(c => !c.is_blocked && c.is_active).length, color: "success" },
            { label: "Заблокировано", value: children.filter(c => c.is_blocked).length, color: "danger" },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
            >
              <Card variant="outlined" sx={{ borderRadius: "16px", p: 0 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography
                    level="h2"
                    sx={{
                      fontWeight: 800, lineHeight: 1,
                      letterSpacing: "-1px",
                      color: `${s.color}.600`,
                    }}
                  >
                    {s.value}
                  </Typography>
                  <Typography level="body-xs" color="neutral" fontWeight={600} sx={{ mt: 0.5 }}>
                    {s.label}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>
      )}

      {/* Список */}
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm" color="neutral" sx={{ mt: 1.5 }}>
            Загружаем список сотрудников...
          </Typography>
        </Stack>
      ) : children.length === 0 ? (
        <EmptyState onAdd={handleAdd} canAdd={canAddAccess} />
      ) : (
        <Stack spacing={1.5}>
          <AnimatePresence>
            {children.map((child, i) => (
                <ChildAccountCard
                    key={child.id}
                    child={child}
                    idx={i}
                    onEdit={handleEdit}
                    onBlock={handleBlock}
                    onDelete={handleDelete}
                    canEditAccess={canEditAccess}
                    canDeleteAccess={canDeleteAccess}
                />
            ))}
          </AnimatePresence>
        </Stack>
      )}

      {/* Модалка создания/редактирования */}
      <ChildAccountModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditChild(null); }}
        editAccount={editChild}
        onSuccess={loadChildren}
      />
    </Box>
  );
}